const DB_NAME = "hrp-docs"
const STORE = "files"
const VERSION = 1

export function isHrpDocCacheFresh(cached, lastModified) {
    if (!cached || !lastModified) return false
    return cached.lastModified === lastModified
}

const openDb = () => new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION)
    req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
})

export async function readHrpDocCache(key) {
    const db = await openDb()
    try {
        return await new Promise((resolve, reject) => {
            const req = db.transaction(STORE, "readonly").objectStore(STORE).get(key)
            req.onsuccess = () => resolve(req.result || null)
            req.onerror = () => reject(req.error)
        })
    } finally {
        db.close()
    }
}

export async function writeHrpDocCache(entry) {
    const db = await openDb()
    try {
        await new Promise((resolve, reject) => {
            const req = db.transaction(STORE, "readwrite").objectStore(STORE).put(entry, entry.key)
            req.onsuccess = () => resolve()
            req.onerror = () => reject(req.error)
        })
    } finally {
        db.close()
    }
}

export async function loadHrpDocFromCacheOrNetwork(file, fetchRemote, fetchBinary) {
    const cached = await readHrpDocCache(file.key)
    if (isHrpDocCacheFresh(cached, file.lastModified)) {
        if (cached.type === "text") return { ...cached, fromCache: true }
        if (cached.blob?.size) {
            return {
                ...cached,
                type: "url",
                blob: pdfBlob(cached.blob, cached.ext || file.ext, cached.contentType),
                fromCache: true,
            }
        }
    }

    const remote = await fetchRemote(file.key)
    const lastModified = file.lastModified || remote.lastModified || null

    if (remote.type === "text") {
        const entry = {
            key: file.key,
            type: "text",
            name: remote.name,
            ext: remote.ext,
            text: remote.text,
            contentType: remote.contentType,
            lastModified,
        }
        await writeHrpDocCache(entry)
        return { ...entry, fromCache: false }
    }

    const contentType = remote.contentType || contentTypeForExt(remote.ext || file.ext)

    // Presigned S3 URLs render reliably in the iframe. Proxying through
    // /hrp-docs/raw works in local Express but API Gateway corrupts binary bodies.
    if (remote.type === "url" && remote.url) {
        return {
            key: file.key,
            type: "url",
            name: remote.name,
            ext: remote.ext,
            url: remote.url,
            contentType,
            lastModified,
            fromCache: false,
        }
    }

    if (!fetchBinary) {
        throw new Error("Could not download document")
    }

    const res = await fetchBinary(file.key)
    if (!res?.ok) throw new Error("Could not download document")
    const blob = new Blob([await res.arrayBuffer()], { type: contentType })
    const entry = {
        key: file.key,
        type: "url",
        name: remote.name,
        ext: remote.ext,
        blob,
        contentType,
        lastModified,
    }
    await writeHrpDocCache(entry)
    return { ...entry, fromCache: false }
}

function contentTypeForExt(ext) {
    if (ext === "pdf") return "application/pdf"
    return "application/octet-stream"
}

function pdfBlob(blob, ext, contentType) {
    const type = contentType || contentTypeForExt(ext)
    if (!blob || blob.type === type) return blob
    return new Blob([blob], { type })
}
